import { useMemo } from "react";

import { useTheme } from "@emotion/react";
import {
  BrandJustLogo,
  CheckCircle,
  ChevronDown,
  CoinsStacked,
  HexagonFill,
  SpeechBubbleAnchor,
} from "@hl/base-components/lib/assets/icons.generated";
import {
  TEXT_COLOR,
  UTILITY_COLOR,
} from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { UnlockGateResult } from "@hl/shared-features/lib/apollo/graphql.generated";
import {
  Accordion,
  Box,
  Card,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  createStyles,
  Anchor,
} from "@mantine/core";
import { commify } from "ethers/lib/utils";

import useMintState from "~hooks/useMintState";

import SimplePublicUserProfile from "../components/SimplePublicUserProfile";

const useStyles = createStyles(() => ({
  control: {
    padding: "16px",
  },
  item: {
    border: "0px solid transparent",
  },
  content: {
    padding: 0,
  },
}));

export default function MintSaleAccordion({
  handleShowModal,
  unlockGateResult,
}: {
  handleShowModal?: () => void;
  unlockGateResult?: UnlockGateResult | null;
}) {
  const { mintVector } = useMintState();
  const { classes } = useStyles();

  const collectorMessage = (mintVector?.collectorMessage ?? "").trim();
  const showDetails = useMemo(() => {
    return (
      mintVector != null &&
      (collectorMessage != "" ||
        mintVector.maxPerUser > 0 ||
        mintVector.maxPerVector > 0)
    );
  }, [mintVector]);

  //TODO lucass use isPublic field instead
  if (mintVector == null || !!mintVector.onchainMintVectorId) return <></>;
  if (!unlockGateResult?.passed) return <></>;
  if (!mintVector.gateId) return <></>;

  return (
    <Paper shadow="md" radius="md" p="0">
      <Accordion
        radius="xs"
        defaultValue="mintVector"
        classNames={classes}
        chevron={<ChevronDown height={24} width={24} />}
      >
        <Accordion.Item value="mintVector">
          <Accordion.Control>
            <Stack spacing={0}>
              <Text size={"xs"} color={TEXT_COLOR.SECONDARY}>
                You qualify for
              </Text>
              <Text size={"sm"} fw={WEIGHT_BOLD}>
                {mintVector?.name || "Default sale"}
              </Text>
            </Stack>
          </Accordion.Control>
          <Accordion.Panel>
            <Divider />
            <Stack spacing="sm" p="md">
              {showDetails && (
                <>
                  <SaleMessage message={collectorMessage} />
                  <AllocatedTokensText amount={mintVector.maxPerVector} />
                  <LimitPerWalletText amount={mintVector.maxPerUser} />
                </>
              )}
              {unlockGateResult?.passed && handleShowModal && (
                <Group spacing={4}>
                  <Group spacing={8}>
                    <CheckCircle width={16} height={16} />
                    <Text
                      size="xs"
                      component="span"
                      color={TEXT_COLOR.SECONDARY}
                    >
                      Sale unlocked!
                    </Text>
                  </Group>{" "}
                  <Anchor
                    href=""
                    onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                      event.preventDefault();
                      handleShowModal();
                    }}
                    fw={WEIGHT_BOLD}
                    color={TEXT_COLOR.SECONDARY}
                    size="xs"
                  >
                    View requirements
                  </Anchor>
                </Group>
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Paper>
  );
}

function AllocatedTokensText(props: { amount: number }) {
  const { amount } = props;

  if (amount === 0) return <></>;

  return (
    <Group spacing={"sm"} align="center">
      <CoinsStacked height={16} width={16} />
      <Text
        size={"xs"}
        color={TEXT_COLOR.SECONDARY}
        sx={{ flexGrow: 1, flexShrink: 1, flex: 1 }}
      >
        {commify(amount)} tokens have been allocated for this sale
      </Text>
    </Group>
  );
}

function LimitPerWalletText(props: { amount: number }) {
  const { amount } = props;

  if (amount === 0) return <></>;

  return (
    <Group spacing={"sm"} align="center">
      <HexagonFill height={16} width={16} />
      <Text
        size={"xs"}
        color={TEXT_COLOR.SECONDARY}
        sx={{ flexGrow: 1, flexShrink: 1, flex: 1 }}
      >
        Each collector who qualifies for this sale can mint up to {amount}{" "}
        tokens
      </Text>
    </Group>
  );
}

function SaleMessage(props: { message: string | null }) {
  const { message: message } = props;
  const { collection } = useMintState();
  const theme = useTheme();

  if (message == null || message.trim() === "") return <></>;

  return (
    <Stack spacing={"sm"}>
      <Box sx={{ position: "relative", overflow: "visible" }}>
        <Card
          radius={"md"}
          shadow="0"
          withBorder={false}
          bg={UTILITY_COLOR.TABLE_ROW_BACKGROUND}
        >
          <Text size={"sm"}>{message}</Text>
        </Card>
        <SpeechBubbleAnchor
          color={theme.colors.tableRowBackground[0]}
          style={{
            position: "absolute",
            right: 32,
            bottom: -6,
          }}
        />
      </Box>

      <Group position="right">
        {/* TODO jj: check if creator reserves! */}
        {1234 ? (
          <SimplePublicUserProfile
            avatarSize={16}
            addressOrEns={
              collection?.creatorAddresses?.[0].address ||
              collection?.creatorEns
            }
          />
        ) : (
          <Group position="right" spacing={"xs"} align="center">
            <BrandJustLogo height={12} width={18} />
            <Text size={"sm"}>Highlight</Text>
          </Group>
        )}
      </Group>
    </Stack>
  );
}
